// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Check,
  Clock,
  Copy,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  PlusCircle,
  User,
  Users,
  XCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  cancelPublicReservaByToken,
  getPublicReservaByToken,
  getPublicReservoSettings,
  listPublicHorarios,
  listPublicSalas,
  updatePublicReservaByToken,
} from '../../../../../services/publicReservoApi';
import { formatDateToLocalISO, getReservoDayOfWeek } from '../../utils/date.ts';
import '../../styles/public-reservation-manage.css';

type PublicReserva = {
  id: string;
  public_token?: string;
  link_gerenciamento?: string | null;
  restaurante_nome?: string | null;
  restaurante_slug?: string | null;
  sala_id?: string | number | null;
  sala_nome?: string | null;
  data?: string | null;
  hora?: string | null;
  numero_pessoas?: number | string | null;
  nome_cliente?: string | null;
  telefone_cliente?: string | null;
  email_cliente?: string | null;
  nascimento_cliente?: string | null;
  obs?: string | null;
  status?: string | null;
  status_cliente?: string | null;
};

type SalaOption = {
  id: string | number;
  nome?: string;
  name?: string;
  min_pessoas_por_reserva?: number | null;
  max_pessoas_por_reserva?: number | null;
};

type HorarioOption = {
  dia_semana: number;
  hora_inicio: string;
  hora_fim: string;
  aberto?: boolean;
};

type ReservationForm = {
  data: string;
  hora: string;
  numero_pessoas: string;
  sala_id: string;
  nome_cliente: string;
  telefone_cliente: string;
  email_cliente: string;
  nascimento_cliente: string;
  obs: string;
};

const FINAL_STATUSES = new Set(['cancelada', 'no_show', 'concluida', 'finalizada', 'finalizado']);

const normalizeTimeForInput = (value?: string | null) => {
  if (!value) return '';
  return String(value).slice(0, 5);
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('pt-BR');
};

const isDateBeforeToday = (value: string | null | undefined, todayIso: string) => {
  const normalized = String(value || '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return false;
  return normalized < todayIso;
};

const formatStatus = (value?: string | null) => {
  const normalized = String(value || '').trim().toLowerCase();
  const labels: Record<string, string> = {
    confirmada: 'Confirmada',
    pendente: 'Pendente',
    cancelada: 'Cancelada',
    no_show: 'Não compareceu',
    concluida: 'Concluída',
    finalizada: 'Finalizada',
    finalizado: 'Finalizado',
  };
  return labels[normalized] || 'Reserva';
};

const setQueryParam = (
  params: URLSearchParams,
  key: string,
  value?: string | number | null,
) => {
  const normalized = value !== null && value !== undefined ? String(value).trim() : '';
  if (normalized) {
    params.set(key, normalized);
  }
};

const buildNewReservationLink = (reserva: PublicReserva) => {
  const slug = String(reserva.restaurante_slug || '').trim();
  if (!slug) return '';

  const params = new URLSearchParams();
  setQueryParam(params, 'nome', reserva.nome_cliente);
  setQueryParam(params, 'telefone', reserva.telefone_cliente);
  setQueryParam(params, 'email', reserva.email_cliente);
  setQueryParam(params, 'nascimento', reserva.nascimento_cliente);
  setQueryParam(params, 'pessoas', reserva.numero_pessoas);

  const query = params.toString();
  return `/reservar/${encodeURIComponent(slug)}${query ? `?${query}` : ''}`;
};

const timeToMinutes = (timeString: string) => {
  const [hours, minutes] = String(timeString).slice(0, 5).split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return NaN;
  return (hours * 60) + minutes;
};

const minutesToTime = (totalMinutes: number) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const generateTimeSlots = (start: string, end: string, interval: number) => {
  const startMinutes = timeToMinutes(start);
  const endMinutes = timeToMinutes(end);
  const safeInterval = Number.isFinite(interval) && interval > 0 ? interval : 30;
  if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes) || endMinutes < startMinutes) return [];

  const slots: string[] = [];
  for (let minutes = startMinutes; minutes < endMinutes; minutes += safeInterval) {
    slots.push(minutesToTime(minutes));
  }

  const finalSlot = minutesToTime(endMinutes);
  if (!slots.includes(finalSlot)) {
    slots.push(finalSlot);
  }

  return slots;
};

const buildInitialForm = (reserva: PublicReserva): ReservationForm => ({
  data: reserva.data || '',
  hora: normalizeTimeForInput(reserva.hora),
  numero_pessoas: reserva.numero_pessoas ? String(reserva.numero_pessoas) : '1',
  sala_id: reserva.sala_id !== null && reserva.sala_id !== undefined ? String(reserva.sala_id) : '',
  nome_cliente: reserva.nome_cliente || '',
  telefone_cliente: reserva.telefone_cliente || '',
  email_cliente: reserva.email_cliente || '',
  nascimento_cliente: reserva.nascimento_cliente || '',
  obs: reserva.obs || '',
});

const PublicReservationManage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [reserva, setReserva] = useState<PublicReserva | null>(null);
  const [form, setForm] = useState<ReservationForm | null>(null);
  const [salas, setSalas] = useState<SalaOption[]>([]);
  const [horarios, setHorarios] = useState<HorarioOption[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  useEffect(() => {
    const loadReserva = async () => {
      if (!token) return;

      try {
        setLoading(true);
        const data = await getPublicReservaByToken(token);
        setReserva(data);
        setForm(buildInitialForm(data));
      } catch (error: any) {
        toast.error(error?.response?.data?.error || 'Reserva não encontrada.');
      } finally {
        setLoading(false);
      }
    };

    loadReserva();
  }, [token]);

  useEffect(() => {
    const loadRestaurantData = async () => {
      const slug = reserva?.restaurante_slug;
      if (!slug) return;

      try {
        const [salasData, horariosData, settingsData] = await Promise.all([
          listPublicSalas(slug),
          listPublicHorarios(slug),
          getPublicReservoSettings(slug),
        ]);
        setSalas(Array.isArray(salasData) ? salasData : []);
        setHorarios(Array.isArray(horariosData) ? horariosData : []);
        setSettings(settingsData || {});
      } catch (error) {
        console.error('Erro ao carregar dados públicos da reserva:', error);
      }
    };

    loadRestaurantData();
  }, [reserva?.restaurante_slug]);

  const todayDateValue = useMemo(() => formatDateToLocalISO(new Date()), []);
  const normalizedStatus = String(reserva?.status || '').trim().toLowerCase();
  const reservationIsPast = isDateBeforeToday(reserva?.data, todayDateValue);
  const canManage = Boolean(reserva) && !FINAL_STATUSES.has(normalizedStatus) && !reservationIsPast;
  const newReservationLink = useMemo(
    () => reserva ? buildNewReservationLink(reserva) : '',
    [reserva],
  );

  const maxDate = useMemo(() => {
    const advanceDays = Number.parseInt(String(settings?.reservo_prazo_reserva_dias || ''), 10);
    if (!Number.isFinite(advanceDays) || advanceDays <= 0) return undefined;
    const target = new Date();
    target.setHours(0, 0, 0, 0);
    target.setDate(target.getDate() + advanceDays);
    return formatDateToLocalISO(target);
  }, [settings]);

  const availableTimes = useMemo(() => {
    if (!form?.data) return [];
    const selectedDate = new Date(`${form.data}T00:00:00`);
    if (Number.isNaN(selectedDate.getTime())) return [];

    const dayOfWeek = getReservoDayOfWeek(selectedDate);
    const interval = Number.parseInt(String(settings?.reservo_intervalo_minutos || 30), 10) || 30;
    const times = horarios
      .filter((horario) => Number(horario.dia_semana) === dayOfWeek && horario.aberto !== false)
      .flatMap((horario) => generateTimeSlots(horario.hora_inicio, horario.hora_fim, interval));

    const uniqueTimes = Array.from(new Set(times)).sort();
    if (form.hora && !uniqueTimes.includes(form.hora)) {
      uniqueTimes.push(form.hora);
      uniqueTimes.sort();
    }
    return uniqueTimes;
  }, [form?.data, form?.hora, horarios, settings]);

  const selectedSala = useMemo(() => (
    salas.find((sala) => String(sala.id) === String(form?.sala_id || '')) || null
  ), [form?.sala_id, salas]);

  const handleFormChange = (field: keyof ReservationForm, value: string) => {
    setForm((current) => current ? { ...current, [field]: value } : current);
  };

  const resetForm = () => {
    if (!reserva) return;
    setForm(buildInitialForm(reserva));
    setEditing(false);
  };

  const validateForm = () => {
    if (!form) return false;
    if (!form.data || !form.hora || !form.sala_id) {
      toast.warning('Informe data, horário e salão.');
      return false;
    }
    if (!form.nome_cliente.trim()) {
      toast.warning('Informe o nome do cliente.');
      return false;
    }
    if (!form.telefone_cliente.trim()) {
      toast.warning('Informe o telefone do cliente.');
      return false;
    }

    const guestCount = Number.parseInt(form.numero_pessoas, 10);
    if (!Number.isFinite(guestCount) || guestCount <= 0) {
      toast.warning('Informe o número de pessoas.');
      return false;
    }

    const minGuests = selectedSala?.min_pessoas_por_reserva || null;
    const maxGuests = selectedSala?.max_pessoas_por_reserva || null;
    if (minGuests && guestCount < Number(minGuests)) {
      toast.warning(`Número mínimo de pessoas para este salão: ${minGuests}.`);
      return false;
    }
    if (maxGuests && guestCount > Number(maxGuests)) {
      toast.warning(`Número máximo de pessoas para este salão: ${maxGuests}.`);
      return false;
    }

    return true;
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token || !form || !validateForm()) return;

    try {
      setSaving(true);
      const updated = await updatePublicReservaByToken(token, {
        data: form.data,
        hora: form.hora,
        sala_id: form.sala_id,
        numero_pessoas: Number.parseInt(form.numero_pessoas, 10),
        nome_cliente: form.nome_cliente.trim(),
        telefone_cliente: form.telefone_cliente.trim(),
        email_cliente: form.email_cliente.trim() || null,
        nascimento_cliente: form.nascimento_cliente || null,
        obs: form.obs.trim() || null,
      });
      setReserva(updated);
      setForm(buildInitialForm(updated));
      setEditing(false);
      toast.success('Reserva alterada com sucesso.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível alterar a reserva.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelReserva = async () => {
    if (!token) return;

    try {
      setSaving(true);
      const canceled = await cancelPublicReservaByToken(token);
      setReserva(canceled);
      setForm(buildInitialForm(canceled));
      setCancelModalOpen(false);
      setEditing(false);
      toast.success('Reserva cancelada.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Não foi possível cancelar a reserva.');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = async () => {
    const link = reserva?.link_gerenciamento || window.location.href;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copiado.');
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  if (loading) {
    return (
      <div className="public-reservation-manage">
        <div className="reservation-manage-panel reservation-manage-loading">
          <Loader2 className="reservation-manage-spin" size={28} />
          <span>Carregando reserva...</span>
        </div>
      </div>
    );
  }

  if (!reserva || !form) {
    return (
      <div className="public-reservation-manage">
        <div className="reservation-manage-panel">
          <h1>Reserva não encontrada</h1>
          <p>Verifique se o link recebido está completo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="public-reservation-manage">
      <div className="reservation-manage-panel">
        <div className="reservation-manage-header">
          <div>
            <span className={`reservation-status reservation-status-${normalizedStatus || 'default'}`}>
              {formatStatus(reserva.status)}
            </span>
            <h1>{reserva.restaurante_nome || 'Sua reserva'}</h1>
          </div>
          <button type="button" className="reservation-icon-button" onClick={copyLink} title="Copiar link da reserva">
            <Copy size={18} />
          </button>
        </div>

        {!editing && (
          <>
            <div className="reservation-summary-grid">
              <div className="reservation-summary-item">
                <Calendar size={20} />
                <span>Data</span>
                <strong>{formatDate(reserva.data)}</strong>
              </div>
              <div className="reservation-summary-item">
                <Clock size={20} />
                <span>Horário</span>
                <strong>{normalizeTimeForInput(reserva.hora) || '-'}</strong>
              </div>
              <div className="reservation-summary-item">
                <Users size={20} />
                <span>Pessoas</span>
                <strong>{reserva.numero_pessoas || '-'}</strong>
              </div>
              <div className="reservation-summary-item">
                <MapPin size={20} />
                <span>Salão</span>
                <strong>{reserva.sala_nome || '-'}</strong>
              </div>
            </div>

            <div className="reservation-customer-lines">
              <div><User size={18} /> {reserva.nome_cliente || '-'}</div>
              <div><Phone size={18} /> {reserva.telefone_cliente || '-'}</div>
              {reserva.email_cliente && <div><Mail size={18} /> {reserva.email_cliente}</div>}
            </div>

            {reserva.obs && (
              <div className="reservation-note">
                <span>Observação</span>
                <p>{reserva.obs}</p>
              </div>
            )}

            {canManage ? (
              <div className="reservation-actions">
                <button
                  type="button"
                  className="reservation-action-button reservation-action-primary"
                  onClick={() => setEditing(true)}
                >
                  <Pencil size={18} />
                  Alterar reserva
                </button>
                <button
                  type="button"
                  className="reservation-action-button reservation-action-danger"
                  onClick={() => setCancelModalOpen(true)}
                >
                  <XCircle size={18} />
                  Cancelar reserva
                </button>
              </div>
            ) : (
              <div className="reservation-final-state">
                <p>
                  Esta reserva não pode mais ser alterada. Para escolher uma nova data,
                  faça uma nova reserva.
                </p>
                <div className="reservation-actions">
                  {newReservationLink && (
                    <a
                      className="reservation-action-button reservation-action-primary"
                      href={newReservationLink}
                    >
                      <PlusCircle size={18} />
                      Nova reserva
                    </a>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {editing && (
          <form className="reservation-edit-form" onSubmit={handleSave}>
            <div className="reservation-form-grid">
              <label>
                <span>Data</span>
                <input
                  type="date"
                  value={form.data}
                  min={todayDateValue}
                  max={maxDate}
                  onChange={(event) => handleFormChange('data', event.target.value)}
                />
              </label>
              <label>
                <span>Horário</span>
                <select
                  value={form.hora}
                  onChange={(event) => handleFormChange('hora', event.target.value)}
                >
                  <option value="">Selecione</option>
                  {availableTimes.map((time) => (
                    <option key={time} value={time}>{time}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Salão</span>
                <select
                  value={form.sala_id}
                  onChange={(event) => handleFormChange('sala_id', event.target.value)}
                >
                  <option value="">Selecione</option>
                  {salas.map((sala) => (
                    <option key={String(sala.id)} value={String(sala.id)}>
                      {sala.nome || sala.name || `Salão ${sala.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Pessoas</span>
                <input
                  type="number"
                  min="1"
                  value={form.numero_pessoas}
                  onChange={(event) => handleFormChange('numero_pessoas', event.target.value)}
                />
              </label>
              <label>
                <span>Nome</span>
                <input
                  type="text"
                  value={form.nome_cliente}
                  onChange={(event) => handleFormChange('nome_cliente', event.target.value)}
                />
              </label>
              <label>
                <span>Telefone</span>
                <input
                  type="tel"
                  value={form.telefone_cliente}
                  onChange={(event) => handleFormChange('telefone_cliente', event.target.value)}
                />
              </label>
              <label>
                <span>E-mail</span>
                <input
                  type="email"
                  value={form.email_cliente}
                  onChange={(event) => handleFormChange('email_cliente', event.target.value)}
                />
              </label>
              <label>
                <span>Nascimento</span>
                <input
                  type="date"
                  value={form.nascimento_cliente}
                  onChange={(event) => handleFormChange('nascimento_cliente', event.target.value)}
                />
              </label>
            </div>

            <label className="reservation-full-field">
              <span>Observação</span>
              <textarea
                value={form.obs}
                rows={4}
                onChange={(event) => handleFormChange('obs', event.target.value)}
              />
            </label>

            <div className="reservation-actions">
              <button
                type="button"
                className="reservation-action-button reservation-action-secondary"
                onClick={resetForm}
                disabled={saving}
              >
                <ArrowLeft size={18} />
                Voltar
              </button>
              <button
                type="submit"
                className="reservation-action-button reservation-action-primary"
                disabled={saving}
              >
                {saving ? <Loader2 className="reservation-manage-spin" size={18} /> : <Check size={18} />}
                Salvar alterações
              </button>
            </div>
          </form>
        )}
      </div>

      {cancelModalOpen && (
        <div className="reservation-modal-backdrop" role="presentation">
          <div className="reservation-modal" role="dialog" aria-modal="true" aria-labelledby="cancel-reservation-title">
            <h2 id="cancel-reservation-title">Cancelar reserva</h2>
            <p>A reserva será marcada como cancelada e deixará de contar na agenda do restaurante.</p>
            <div className="reservation-modal-actions">
              <button
                type="button"
                className="reservation-action-button reservation-action-secondary"
                onClick={() => setCancelModalOpen(false)}
                disabled={saving}
              >
                Voltar
              </button>
              <button
                type="button"
                className="reservation-action-button reservation-action-danger"
                onClick={handleCancelReserva}
                disabled={saving}
              >
                {saving ? <Loader2 className="reservation-manage-spin" size={18} /> : <XCircle size={18} />}
                Confirmar cancelamento
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicReservationManage;
